import groupCheeseMarket01 from '@/assets/mock/group/group-cheese-market-01.jpg';
import groupCheeseMarket02 from '@/assets/mock/group/group-cheese-market-02.jpg';
import groupCheeseMarket03 from '@/assets/mock/group/group-cheese-market-03.jpg';
import groupCheeseMarket04 from '@/assets/mock/group/group-cheese-market-04.jpg';
import groupCheeseMarket05 from '@/assets/mock/group/group-cheese-market-05.jpg';
import groupCheeseMarket06 from '@/assets/mock/group/group-cheese-market-06.jpg';
import portrait01 from '@/assets/mock/portrait/portrait-01.jpg';
import portrait02 from '@/assets/mock/portrait/portrait-02.jpg';
import portrait03 from '@/assets/mock/portrait/portrait-03.jpg';
import portrait04 from '@/assets/mock/portrait/portrait-04.jpg';
import portrait05 from '@/assets/mock/portrait/portrait-05.jpg';
import portrait06 from '@/assets/mock/portrait/portrait-06.jpg';
import portrait07 from '@/assets/mock/portrait/portrait-07.jpg';
import portrait08 from '@/assets/mock/portrait/portrait-08.jpg';
import portrait09 from '@/assets/mock/portrait/portrait-09.jpg';
import portrait10 from '@/assets/mock/portrait/portrait-10.jpg';
import portrait11 from '@/assets/mock/portrait/portrait-11.jpg';
import portrait12 from '@/assets/mock/portrait/portrait-12.jpg';
import type {
  EntrGroup,
  EntrPhoto,
  EntrSession,
  PriceConfig,
} from '@/domain/types';

const GROUP_PLACEHOLDERS = [
  groupCheeseMarket01,
  groupCheeseMarket02,
  groupCheeseMarket03,
  groupCheeseMarket04,
  groupCheeseMarket05,
  groupCheeseMarket06,
] as const;

const PORTRAIT_PLACEHOLDERS = [
  portrait01,
  portrait02,
  portrait03,
  portrait04,
  portrait05,
  portrait06,
  portrait07,
  portrait08,
  portrait09,
  portrait10,
  portrait11,
  portrait12,
] as const;

function portraitPlaceholder(index: number): string {
  const url = PORTRAIT_PLACEHOLDERS[index % PORTRAIT_PLACEHOLDERS.length];
  if (!url) {
    throw new Error('No ENTR portrait placeholders configured');
  }

  return url;
}

export const MOCK_PRICES: PriceConfig = {
  currency: 'EUR',
  groupPhotoCents: 995,
  portraitCents: 695,
};

export const MOCK_GROUPS: EntrGroup[] = [
  {
    id: 'fdb',
    name: 'Familie de Bakker',
    displayName: 'De Bakker',
    memberCount: 4,
    coverUrl: portraitPlaceholder(0),
    voucher: { kind: 'none' },
    photoState: 'ready',
  },
  {
    id: 'vw',
    name: 'Vrienden van Wilma',
    displayName: 'Wilma',
    memberCount: 3,
    coverUrl: portraitPlaceholder(1),
    voucher: { kind: 'prepaid', prepaidPhotoCount: 2, usedPhotoCount: 0 },
    photoState: 'ready',
  },
  {
    id: 'svdl',
    name: 'Sebastiaan van der Linden',
    displayName: 'Sebastiaan',
    memberCount: 2,
    coverUrl: portraitPlaceholder(2),
    voucher: { kind: 'none' },
    photoState: 'ready',
  },
  {
    id: 'sve',
    name: 'Sandra van Egmond',
    displayName: 'Sandra',
    memberCount: 5,
    coverUrl: portraitPlaceholder(3),
    voucher: { kind: 'none' },
    photoState: 'ready',
  },
  {
    id: 'hdb',
    name: 'Henk de Boer',
    displayName: 'Henk',
    memberCount: 2,
    coverUrl: portraitPlaceholder(4),
    voucher: { kind: 'prepaid', prepaidPhotoCount: 1, usedPhotoCount: 0 },
    photoState: 'ready',
  },
  {
    id: 'ns',
    name: 'Natashja Smit',
    displayName: 'Natashja',
    memberCount: 3,
    coverUrl: portraitPlaceholder(5),
    voucher: { kind: 'none' },
    photoState: 'ready',
  },
  {
    id: 'mr',
    name: 'Mila en Ravi',
    displayName: 'Mila',
    memberCount: 2,
    coverUrl: portraitPlaceholder(6),
    voucher: { kind: 'prepaid', prepaidPhotoCount: 3, usedPhotoCount: 1 },
    photoState: 'ready',
  },
  {
    id: 'ld',
    name: 'Lotte de Vries',
    displayName: 'Lotte',
    memberCount: 4,
    coverUrl: portraitPlaceholder(7),
    voucher: { kind: 'none' },
    photoState: 'not-ready',
  },
  {
    id: 'bk',
    name: 'Bas en Koen',
    displayName: 'Bas',
    memberCount: 2,
    coverUrl: portraitPlaceholder(8),
    voucher: { kind: 'none' },
    photoState: 'ready',
  },
  {
    id: 'am',
    name: 'Anne-Marie',
    displayName: 'Anne-Marie',
    memberCount: 1,
    coverUrl: portraitPlaceholder(9),
    voucher: { kind: 'prepaid', prepaidPhotoCount: 1, usedPhotoCount: 0 },
    photoState: 'ready',
  },
  {
    id: 'jp',
    name: 'Jasper',
    displayName: 'Jasper',
    memberCount: 3,
    coverUrl: portraitPlaceholder(10),
    voucher: { kind: 'none' },
    photoState: 'ready',
  },
  {
    id: 'nk',
    name: 'Noor en Kees',
    displayName: 'Noor',
    memberCount: 2,
    coverUrl: portraitPlaceholder(11),
    voucher: { kind: 'unknown' },
    photoState: 'ready',
  },
];

const GROUP_PHOTOS = mockPhotos(
  GROUP_PLACEHOLDERS.slice(0, 5),
  'group',
  1536 / 1024
);

const PORTRAITS = mockPhotos(PORTRAIT_PLACEHOLDERS, 'portrait', 832 / 1216);

function mockPhotos(
  urls: readonly string[],
  type: EntrPhoto['type'],
  aspectRatio: number
): EntrPhoto[] {
  return urls.map((url, index) => {
    const id = `${type}-${index + 1}`;
    return { id, type, url, aspectRatio, printImageUuid: `mock-${id}` };
  });
}

export function createMockSession(
  group: EntrGroup,
  source: EntrSession['source'],
  ticketCode?: string
): EntrSession {
  const forGroup = (photo: EntrPhoto): EntrPhoto => ({
    ...photo,
    id: `${group.id}-${photo.id}`,
    printImageUuid: `${group.id}-${photo.printImageUuid}`,
  });
  return {
    id: `entr-${source}-${group.id}-${Date.now()}`,
    source,
    group,
    groupPhotos: GROUP_PHOTOS.map(forGroup),
    portraits: PORTRAITS.map(forGroup),
    ticketCode,
    voucher: group.voucher,
  };
}

export const MOCK_TICKET_TO_GROUP: Record<string, string> = {
  'SCAN-PREPAID': 'vw',
  'SCAN-NO-PREPAID': 'fdb',
  'SCAN-OVERAGE': 'hdb',
  'SCAN-PHOTO-NOT-READY': 'ld',
  TICKET123: 'vw',
};
